import {
  BMComponentsType,
  BMInputField,
  BMPrimitives,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcSystemCrossClose from "@traveloka/icon-kit-web/react/IcSystemCrossClose";
import { Pressable, StyleSheet } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { FullScreenDialog } from "../../../../components";
import BMView from "../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";
import { useHotelAutocomplete } from "../../../../providers/api/HotelAutocompleteContext";
import { appendTestId } from "../../../../utils";
import {
  PreviousHotelSearchData,
  SelectedPreviousHotelSearchData,
} from "../HotelSearchForm";
import { HotelSearchFormResource } from "../types";
import EmptyResult from "./EmptyResult";
import EmptySearch from "./EmptySearch";
import SearchResult from "./SearchResult";
import SearchResultLoader from "./SearchResultLoader/SearchResultLoader";

type Props = {
  isVisible: boolean;
  closeDialog: () => void;
  resource: HotelSearchFormResource;
  previousSearchData?: PreviousHotelSearchData[];
  selectPreviousSearchData: (
    searchData: SelectedPreviousHotelSearchData
  ) => void;
};

const testID = "hotel-city_search";

export default function HotelCitySearch(props: Props) {
  const {
    isVisible,
    closeDialog,
    resource,
    previousSearchData,
    selectPreviousSearchData,
  } = props;
  const { BMComponents } = useBMTheme();
  const { searchValue, handleSearchValueChange } = useHotelAutocomplete();
  const { CorporateHotelSearchForm } = useMobileContentResource();
  const styles = createStyles(BMComponents);
  const insets = useSafeAreaInsets();

  return (
    <FullScreenDialog style={styles.dialog} visible={isVisible}>
      <BMView
        row
        spacing="md"
        align="center"
        style={[
          styles.container,
          { paddingTop: BMPrimitives.BMSize.md + insets.top },
        ]}
      >
        <BMView style={styles.input}>
          <BMInputField
            testID={appendTestId(testID, "input")}
            placeholder={CorporateHotelSearchForm.hotelSearchPlaceholder}
            label=""
            value={searchValue}
            onChange={(e) => handleSearchValueChange(e.nativeEvent.text)}
            buttonInsetProps={
              searchValue
                ? {
                    label: CorporateHotelSearchForm.clear,
                    onPress: () => handleSearchValueChange(""),
                  }
                : undefined
            }
          />
        </BMView>
        <Pressable
          testID={appendTestId(testID, "close")}
          style={styles.closeButton}
          onPress={closeDialog}
        >
          <IcSystemCrossClose />
        </Pressable>
      </BMView>
      <ShownComponent
        resource={resource}
        onCloseDialog={closeDialog}
        previousSearchData={previousSearchData}
        selectPreviousSearchData={selectPreviousSearchData}
      />
    </FullScreenDialog>
  );
}

type ShownComponentProps = {
  resource: HotelSearchFormResource;
  onCloseDialog: () => void;
  previousSearchData?: PreviousHotelSearchData[];
  selectPreviousSearchData: (
    searchData: SelectedPreviousHotelSearchData
  ) => void;
};

function ShownComponent(props: ShownComponentProps) {
  const {
    resource,
    onCloseDialog,
    previousSearchData,
    selectPreviousSearchData,
  } = props;

  const {
    searchValue,
    isAutocompleteNoResults,
    isAutoCompleteLoading,
    isAutocompleteError,
    results,
  } = useHotelAutocomplete();

  if (!searchValue)
    return (
      <EmptySearch
        previousSearchData={previousSearchData}
        onCloseDialog={onCloseDialog}
        selectPreviousSearchData={selectPreviousSearchData}
        testID={testID}
      />
    );
  if (isAutoCompleteLoading) return <SearchResultLoader />;
  if (isAutocompleteNoResults)
    return <EmptyResult resource={resource} query={searchValue} />;
  return (
    <SearchResult
      query={searchValue}
      searchResults={results}
      resource={resource}
      isError={isAutocompleteError}
      onCloseDialog={onCloseDialog}
      testID={testID}
    />
  );
}

const createStyles = (BMComponents: BMComponentsType) =>
  StyleSheet.create({
    dialog: {
      backgroundColor: BMComponents.BMStyleSurfaceBgcolorInventory,
    },
    container: {
      paddingHorizontal: BMPrimitives.BMSize.md,
      paddingVertical: BMPrimitives.BMSize.xs,
      borderBottomWidth: BMPrimitives.BMStroke.sm,
      borderBottomColor: BMComponents.BMStyleTextInkPrimaryInvert,
    },
    input: {
      width: "90%",
    },
    closeButton: {
      marginLeft: "auto",
    },
  });
